import React, { useState, useContext, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import apiClient from "../api/client";
import { ArrowLeft, Send, Video, Phone, Check, X, Shield, Lock } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { useEntitlements } from '../context/EntitlementsContext';
import { OnlineDot } from './OnlineDot';

// Backend origin for absolute-path media URLs ("/uploads/audio/x.webm" etc.)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
import ReportBlockModal from './ReportBlockModal';
import useSSE from '../hooks/useSSE';
import { subscribeToEvent, subscribeToMatchMessages } from '../lib/realtime';
import { getRealtimeClient, getSafeChannel, markFirstMessageReceived, markFirstTypingReceived } from '../services/realtime';
import SharePrivateGalleryButton from './SharePrivateGalleryButton';
import { requestVideoCallStart } from '../services/chat';
import VideoLimitModal from './VideoLimitModal';
import { useTypingIndicator } from '../hooks/useTypingIndicator';
import { useReadReceipts } from '../hooks/useReadReceipts';
import { useMessageReactions } from '../hooks/useMessageReactions';
import { useAudioRecorder } from '../hooks/useAudioRecorder';
import { debugLog } from '../utils/debugLog';
import { reportUser, blockUser } from '../api/moderation';
import GiftPickerModal from './GiftPickerModal';
import { useGiftAnimations } from '../hooks/useGiftAnimations';
import { REACTIONS } from './reactions/reactionsConfig';
// FIX #3: Use centralized API client for consistent auth handling
import { api } from '../api/client';
import { cancelUniverseLoading } from '../hooks/useSafeLoader';
import { getEliteUpgradeUrl } from '../utils/entitlements';
import { grantPrivateGalleryAccess } from '../api/privateGallery';
import ComposerAssist from './chat/ComposerAssist';
import SuggestedStarters from './chat/SuggestedStarters';
import InnerWorkModal from './InnerWorkModal';
import { getTodayPrompt } from '../api/innerWork';
import { trackEvent } from '../api/analytics';
import { isEligibleForNudge, markNudgeShown, markNudgeDismissed, markNudgeUsed } from './chat/assistNudge';
import { resolveMessageAuthor } from '../utils/messageAuthor';

/**
 * CANONICAL ID: match_id
 * ====================
 * This component uses `matchId` (from route params) as the canonical identifier.
 * - Route: /chat/:matchId
 * - Ably channel: chat:match:${matchId}
 * - Backend lookup: matches collection by match_id
 * 
 * Do NOT introduce conversation_id as a separate concept here.
 */

/**
 * P4: Conversation Starters - shown only when thread is empty
 */
function ConversationStarters({ onPick }) {
  const starters = [
    "What brought you to UnityLove?",
    "What kind of connection are you looking for?",
    "What's something you're into lately?",
  ];
  return (
    <div className="mb-3 mx-4 rounded-2xl border border-slate-200 dark:border-slate-700 surface-card p-4">
      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">Quick starters 💬</div>
      <div className="flex flex-wrap gap-2">
        {starters.map((s) => (
          <button
            key={s}
            className="rounded-xl border border-slate-200 dark:border-slate-600 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition text-left"
            onClick={() => onPick(s)}
            type="button"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}


// Debug logging gate - runtime check (not module-level) so localStorage changes take effect
const isDebugEnabled = () =>
  process.env.NODE_ENV !== 'production' && localStorage.getItem('chat_debug') === '1';

const log = (...args) => {
  if (isDebugEnabled()) console.log('[Chat]', ...args);
};

// Safe reactions object helper - handles any unexpected shape
const reactionsObj = (message) => {
  const r = message?.reactions;
  return r && typeof r === 'object' && !Array.isArray(r) ? r : {};
};

// Axios config with timeout
const getAxiosConfig = (token) => ({
  headers: { Authorization: `Bearer ${token}` },
  timeout: 10000
});

// Ticket 8: Animated typing indicator component
const TypingIndicator = () => {
  return (
    <div className="chat-typing-indicator text-xs text-gray-600 dark:text-gray-400">
      <span className="mr-1">Typing</span>
      <span className="flex items-center gap-1">
        <span className="chat-typing-dot" />
        <span className="chat-typing-dot" />
        <span className="chat-typing-dot" />
      </span>
    </div>
  );
};

const Chat = () => {
  const { matchId } = useParams();
  const { user, token } = useContext(AuthContext);
  const { hasFeature, getThreshold } = useEntitlements();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Entitlements-based feature checks (from server)
  const canSendVoice = hasFeature('can_send_voice');
  const canUseVideoChat = hasFeature('can_video_chat');
  const videoUnlockThreshold = getThreshold('video_unlock_min_messages_each');
  const voiceUnlockThreshold = getThreshold('voice_unlock_min_messages_each') || 3;
  
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null); // Track load errors
  const [sending, setSending] = useState(false);
  const [otherIsTyping, setOtherIsTyping] = useState(false);
  const [otherOnline, setOtherOnline] = useState(false);
  
  // SAFETY: Timeout to clear loading if something goes wrong (non-stale pattern)
  useEffect(() => {
    if (!loading) return;
    const timeout = setTimeout(() => {
      console.warn('[Chat] Loading timeout - forcing clear');
      setLoading(false);
      setLoadError('Chat took too long to load. Please try again.');
    }, 10000);
    return () => clearTimeout(timeout);
  }, [loading]);
  
  // === VIDEO UNLOCK STATE ===
  const [videoUnlocked, setVideoUnlocked] = useState(false);
  const [videoInviteSent, setVideoInviteSent] = useState(false);
  const [videoInviteReceived, setVideoInviteReceived] = useState(null);
  
  // === VOICE UNLOCK STATE ===
  const [voiceUnlocked, setVoiceUnlocked] = useState(false);
  const [voiceUnlockStatus, setVoiceUnlockStatus] = useState({
    you_sent: 0,
    they_sent: 0,
    required_each: 3,
  });
  
  // Active video call state
  const [activeCall, setActiveCall] = useState(null); // { callId, roomId, isCaller, status, fromUserId, fromUserName }
  
  // Reaction picker state (portal-based for viewport clamping)
  const reactionAnchorRef = useRef(null);
  const [reactionUI, setReactionUI] = useState({
    openFor: null,       // message.id
    top: 0,
    left: 0,
  });

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  // Compute picker position (reusable)
  const computePickerPosition = useCallback(() => {
    if (!reactionAnchorRef.current) return null;
    const rect = reactionAnchorRef.current.getBoundingClientRect();
    const pickerWidth = 340;
    const pickerHeight = 56;
    const padding = 8;
    const vw = window.visualViewport?.width || window.innerWidth;
    const vh = window.visualViewport?.height || window.innerHeight;

    const desiredLeft = rect.left + rect.width / 2 - pickerWidth / 2;
    const left = clamp(desiredLeft, padding, vw - pickerWidth - padding);

    const desiredTop = rect.top - pickerHeight - 10;
    const top = desiredTop >= padding
      ? desiredTop
      : clamp(rect.bottom + 10, padding, vh - pickerHeight - padding);

    return { top, left };
  }, []);

  const openReactionPicker = (messageId, anchorEl) => {
    if (!anchorEl) return;
    reactionAnchorRef.current = anchorEl;
    setReactionUI((prev) => ({ ...prev, openFor: messageId }));
  };

  const closeReactionPicker = () => {
    reactionAnchorRef.current = null;
    setReactionUI({ openFor: null, top: 0, left: 0 });
  };

  // Recompute picker position whenever it opens + on viewport resize/scroll
  useLayoutEffect(() => {
    if (!reactionUI.openFor || !reactionAnchorRef.current) return;

    // Initial position
    const pos = computePickerPosition();
    if (pos) {
      setReactionUI((prev) => ({ ...prev, top: pos.top, left: pos.left }));
    }

    // Recompute on viewport resize (mobile keyboard, orientation change)
    const handleResize = () => {
      const newPos = computePickerPosition();
      if (newPos) {
        setReactionUI((prev) => ({ ...prev, top: newPos.top, left: newPos.left }));
      }
    };

    // Listen to visualViewport for mobile keyboard changes
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', handleResize);
      vv.addEventListener('scroll', handleResize);
    }
    window.addEventListener('scroll', handleResize, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      if (vv) {
        vv.removeEventListener('resize', handleResize);
        vv.removeEventListener('scroll', handleResize);
      }
      window.removeEventListener('scroll', handleResize);
      window.removeEventListener('resize', handleResize);
    };
  }, [reactionUI.openFor, computePickerPosition]);

  const messagesEndRef = useRef(null);
  
  // Video limit state
  const [isStartingVideo, setIsStartingVideo] = useState(false);
  const [showVideoLimitModal, setShowVideoLimitModal] = useState(false);
  const [videoLimitMessage, setVideoLimitMessage] = useState('');
  const [videoLimitRemaining, setVideoLimitRemaining] = useState(null);

  // Ticket 14: Moderation state
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');

  // Private Gallery Grant state
  const [grantingPrivateAccess, setGrantingPrivateAccess] = useState(false);
  const [privateAccessGranted, setPrivateAccessGranted] = useState(false);

  // Ticket 24: Gift modal state
  const [showGiftModal, setShowGiftModal] = useState(false);

  // --- Translation state (for received text messages) ---
  const [translatedById, setTranslatedById] = useState({}); // { [messageId]: string }
  const [translatingById, setTranslatingById] = useState({}); // { [messageId]: boolean }
  const [translateErrorById, setTranslateErrorById] = useState({}); // { [messageId]: string }

  // --- Inner Work modal state ---
  const [innerOpen, setInnerOpen] = useState(false);
  const [innerPrompt, setInnerPrompt] = useState(null);
  const [innerNudgeDismissed, setInnerNudgeDismissed] = useState(false);
  const [nudgeReason, setNudgeReason] = useState(null);

  // Fetch today's Inner Work prompt
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const p = await getTodayPrompt();
        if (mounted) setInnerPrompt(p);
      } catch {
        // Silent fail
      }
    })();
    return () => { mounted = false; };
  }, []);

  // P2: Calculate nudge eligibility (12h inactivity OR +10 messages, 24h cooldown)
  const canShowInnerNudge = React.useMemo(() => {
    if (!user?.id) return false;
    if (innerNudgeDismissed) return false;
    const { eligible, reason } = isEligibleForNudge({
      messages,
      userId: user.id,
      matchId,
    });
    // Store reason for analytics (side effect in useMemo is safe here as it's just state)
    if (reason !== nudgeReason) {
      setNudgeReason(reason);
    }
    return eligible;
  }, [user?.id, matchId, innerNudgeDismissed, messages, nudgeReason]);

  // P2: Track nudge exposure once when it becomes eligible
  useEffect(() => {
    if (!canShowInnerNudge) return;
    if (!user?.id || !matchId) return;
    // Mark shown immediately to avoid flicker / double exposures
    markNudgeShown({
      userId: user.id,
      matchId,
      messagesLength: Array.isArray(messages) ? messages.length : 0,
      reason: nudgeReason,
    });
    trackEvent("assist_nudge_shown", {
      match_id: matchId,
      reason: nudgeReason,
      messages_len: Array.isArray(messages) ? messages.length : 0,
    });
  }, [canShowInnerNudge, user?.id, matchId, messages, nudgeReason]);

  // P2: Handle nudge "Reflect" click
  const showInnerWork = () => {
    if (user?.id && matchId) {
      markNudgeUsed({
        userId: user.id,
        matchId,
        messagesLength: Array.isArray(messages) ? messages.length : 0,
      });
      trackEvent("assist_used", {
        source: "nudge_reflect",
        match_id: matchId,
        messages_len: Array.isArray(messages) ? messages.length : 0,
      });
    }
    setInnerOpen(true);
  };

  // P2: Handle nudge dismiss
  const dismissInnerNudge = () => {
    if (user?.id && matchId) {
      markNudgeDismissed({
        userId: user.id,
        matchId,
        messagesLength: Array.isArray(messages) ? messages.length : 0,
      });
      trackEvent("assist_nudge_dismissed", {
        match_id: matchId,
        reason: nudgeReason,
      });
    }
    setInnerNudgeDismissed(true);
  };

  // Ticket 25: Gift animations
  const { triggerGiftAnimation } = useGiftAnimations();

  // Ably client for real-time features
  const [ablyClient, setAblyClient] = useState(null);

  // Initialize Ably client
  useEffect(() => {
    getRealtimeClient().then(client => setAblyClient(client));
  }, []);

  // Typing indicator hook
  const { isPartnerTyping, sendTyping } = useTypingIndicator({
    ably: ablyClient,
    conversationId: matchId,
    currentUserId: user?.id,
  });

  // Read receipts hook
  const { partnerLastReadId, partnerLastDeliveredId, sendReadReceipt } = useReadReceipts({
    ably: ablyClient,
    conversationId: matchId,
    currentUserId: user?.id,
    token,
  });

  // Ticket 9: Message reactions hook (Option B structure)
  const { toggleReaction } = useMessageReactions({
    ably: ablyClient,
    matchId,
    token,
    onUpdate: (data) => {
      const { message_id, reactions } = data;
      // Safely handle reactions - ensure it's always an object
      const safeReactions = (reactions && typeof reactions === 'object' && !Array.isArray(reactions)) 
        ? reactions 
        : {};
      setMessages((prev) =>
        prev.map((m) => {
          if (String(m.id) !== String(message_id)) return m;
          return {
            ...m,
            reactions: safeReactions ?? {},
          };
        })
      );
    },
  });

  // Ticket 9: Helper functions for reactions (Option B structure)
  const getReactionSummary = (message) => {
    // reactions is now: { "❤️": ["user1", "user2"], "🔥": ["user3"] }
    const reactions = message.reactions || {};
    return Object.entries(reactions).map(([emoji, userIds]) => ({
      emoji,
      count: userIds.length,
    })).filter(r => r.count > 0); // Only show reactions with counts
  };

  const userHasReaction = (message, emoji, userId) => {
    // Check if userId is in the array for this emoji
    const reactions = message.reactions || {};
    const userIds = reactions[emoji] || [];
    return userIds.includes(userId);
  };

  // Ticket 10: Audio recorder hook
  const {
    isRecording,
    durationMs: recordingDuration,
    error: audioError,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useAudioRecorder();

  // State to store the other user's ID
  const [otherUserId, setOtherUserId] = useState(null);

  // Check if we've already granted private gallery access to the other user
  // This makes the "Give Private Access" button show as "✅ Access Granted" if already done
  useEffect(() => {
    if (!otherUserId || !user?.id || privateAccessGranted) return;
    
    (async () => {
      try {
        // Import dynamically to avoid circular deps if needed
        const { listPrivateGalleryRequests } = await import('../api/privateGallery');
        const data = await listPrivateGalleryRequests();
        
        // Check if other user is in our approved list
        const approved = data?.approved || [];
        const hasAccess = approved.some(item => item.viewer_id === otherUserId);
        
        if (hasAccess) {
          setPrivateAccessGranted(true);
          log('Other user already has private gallery access');
        }
      } catch (e) {
        // Non-critical - just don't show the granted state
        log('Could not check private gallery status:', e);
      }
    })();
  }, [otherUserId, user?.id, privateAccessGranted]);
  
  // FAST PATH: Fetch messages first, then lightweight match metadata in background
  useEffect(() => {
    let cancelled = false;

    (async () => {
      log('Route: /chat/' + matchId);
      log('Token present:', !!token);
      
      setLoading(true);
      setLoadError(null);

      try {
        // Guard: Validate required params - check localStorage immediately as backup
        const effectiveToken = token || localStorage.getItem('token');
        if (!effectiveToken) {
          throw new Error('Not logged in - please sign in to view chat');
        }
        if (!matchId) throw new Error('Missing matchId in URL');

        // === FAST PATH: fetch messages FIRST, unblock UI immediately ===
        log('Starting critical fetch for matchId:', matchId);

        const messagesResponse = await api.get(`/messages/${matchId}?limit=50`).catch(err => {
          console.error('[Chat] Messages fetch failed:', err);
          throw err; // messages are critical
        });

        // Handle both response shapes: { items: [...] } or [...]
        const msgs = Array.isArray(messagesResponse.data)
          ? messagesResponse.data
          : (messagesResponse.data?.items?.reverse() ?? []);

        if (cancelled) return;

        log('messages loaded:', msgs.length);
        setMessages(msgs);

        // Derive other user id quickly
        const otherMessage = msgs.find(m => m.sender_id !== user?.id);
        if (otherMessage) setOtherUserId(otherMessage.sender_id);

        // ✅ UNBLOCK UI ASAP (this is the whole point)
        log('critical fetch complete — clearing loading');
        setLoading(false);

        // background: LIGHTWEIGHT match metadata fetch (with timeout)
        api.get(`/chat/meta/${matchId}`, { timeout: 8000 })
          .then((res) => {
            if (cancelled) return;
            log('match meta loaded:', res.data?.profile?.names?.[0] || 'unknown');
            setMatch(res.data || null);
            // Set video unlock status from match meta (coerce to boolean for safety)
            if (res?.data?.video_unlocked !== undefined) {
              setVideoUnlocked(Boolean(res.data.video_unlocked));
              log('Video unlock status from meta:', Boolean(res.data.video_unlocked));
            }
          })
          .catch((err) => {
            // Fallback to /matches only if lightweight endpoint fails (backwards compat)
            log('chat/meta failed, falling back to /matches:', err?.message);
            api.get('/matches')
              .then((res) => {
                if (cancelled) return;
                const list = Array.isArray(res.data) ? res.data : [];
                const currentMatch = list.find(m => (m.match_id || m.id) === matchId);
                log('found match via fallback:', currentMatch?.profile?.names?.[0] || 'not found');
                setMatch(currentMatch || null);
                // Set video unlock status from match data (coerce to boolean)
                if (currentMatch?.video_unlocked !== undefined) {
                  setVideoUnlocked(Boolean(currentMatch.video_unlocked));
                }
              })
              .catch((err2) => {
                console.warn('[Chat] Both match fetches failed:', err2?.message);
              });
          });

        // ---- fire-and-forget mark-read ----
        api.post('/chat/mark-read', { match_id: matchId })
          .then(() => {
            log('conversation marked as read');
            if (window.refreshNotificationSummary) {
              window.refreshNotificationSummary();
              log('triggered badge refresh');
            }
          })
          .catch(err => log('Mark read failed (non-blocking):', err.message));

        log('data fetch complete');
        
      } catch (error) {
        console.error('[Chat] load failed:', error);
        console.error('[Chat] Error details:', {
          status: error?.response?.status,
          data: error?.response?.data,
          message: error?.message
        });
        
        if (!cancelled) {
          // Distinguish error types for better user feedback
          const status = error?.response?.status;
          let errorMsg = error?.response?.data?.detail || error?.message || 'Failed to load chat';
          
          if (status === 401) {
            errorMsg = 'Session expired - please log in again';
          } else if (status === 403) {
            errorMsg = 'Access denied - you may not have permission to view this chat';
          } else if (status === 404) {
            errorMsg = 'This conversation isn\'t available anymore.';
          } else if (status >= 500) {
            errorMsg = 'Server error - please try again in a moment';
          } else if (error?.message?.includes('HTML')) {
            errorMsg = 'Server configuration error - please refresh the page';
            console.error('[Chat] Server returned HTML instead of JSON - possible routing issue');
          }
          
          setLoadError(errorMsg);
          setLoading(false);
        }
      } finally {
        // Last-resort safety: ensure loading is never stuck
        if (!cancelled) {
          setLoading((v) => (v ? false : v));
        }
      }
    })();

    return () => { 
      cancelled = true;
      log('unmount cleanup');

      // Critical: when Chat closes (overlay/back), force Universe to clear any stuck loading state
      cancelUniverseLoading({ source: "Chat:unmount" });
    };
  }, [matchId, token, user?.id]);


  // ---------------------------------------------------------------------------
  // Online status polling for the other user
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!match || !user) return;
    const otherId =
      match.user1_id === user.id ? match.user2_id : match.user1_id;
    if (!otherId) return;

    let cancelled = false;

    const fetchStatus = async () => {
      if (cancelled) return;
      try {
        // FIX #3: Use centralized api client
        const res = await api.post('/users/online-status', { user_ids: [otherId] });
        const entry = res.data?.statuses?.[0];
        if (entry && !cancelled) {
          setOtherOnline(!!entry.is_online);
        }
      } catch (err) {
        // ignore
      }
    };

    fetchStatus();
    const id = setInterval(fetchStatus, 30000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [match, user, token]);

  // ---------------------------------------------------------------------------
  // Typing indicators over Ably
  // ---------------------------------------------------------------------------

  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const ablyChannelRef = useRef(null);
  const roomEpochRef = useRef(0); // Prevents stale callbacks after room switch

  const sendTypingEvent = (isTyping) => {
    if (!ablyChannelRef.current || !user) return;
    try {
      ablyChannelRef.current.publish("typing", {
        type: "typing",
        match_id: matchId,
        from_user_id: user.id,
        is_typing: isTyping,
        ts: Date.now(),
      });
    } catch {
      // ignore
    }
  };

  const handleInputChange = (e) => {
    setNewMessage(e.target.value);

    const now = Date.now();
    if (now - lastTypingSentRef.current > 2000) {
      sendTypingEvent(true);
      lastTypingSentRef.current = now;
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingEvent(false);
    }, 3000);
  };


  // Real-time updates via Ably (primary) - UNIFIED
  useEffect(() => {
    if (!matchId || !ablyClient) return;
    
    // Increment epoch to invalidate any stale callbacks
    roomEpochRef.current += 1;
    const currentEpoch = roomEpochRef.current;
    
    log(`Setting up Ably subscription for match_id="${matchId}" (epoch ${currentEpoch})`);
    
    const channelName = `chat:match:${matchId}`;
    // CRITICAL FIX: Use getSafeChannel to prevent DETACHED errors from crashing React
    const channel = getSafeChannel(ablyClient, channelName);
    if (!channel) return; // Safety check
    ablyChannelRef.current = channel;
    
    const unsubscribe = subscribeToMatchMessages(matchId, (data) => {
      // CRITICAL: Ignore stale callbacks from previous room
      if (currentEpoch !== roomEpochRef.current) {
        log(`Ignoring stale message from epoch ${currentEpoch} (current: ${roomEpochRef.current})`);
        return;
      }
      log('RT got message-created:', data?.id);
      
      // Mark first message received for Ably metrics (user-perceived latency)
      markFirstMessageReceived();
      
      // Update video unlock status if present (coerce to boolean for safety)
      if (data.video_unlocked !== undefined) {
        setVideoUnlocked(Boolean(data.video_unlocked));
        log(`Video unlock status updated to:`, Boolean(data.video_unlocked));
      }
      
      // Update voice unlock status if present
      if (typeof data.voice_unlocked === 'boolean') {
        setVoiceUnlocked(data.voice_unlocked);
        log(`Voice unlock status updated to:`, data.voice_unlocked);
      }
      if (data.voice_unlock_status) {
        setVoiceUnlockStatus(data.voice_unlock_status);
        log(`Voice unlock counts:`, data.voice_unlock_status);
      }
      
      // Handle video call offer (incoming call)
      if (data.type === 'video_call_offer') {
        log('Incoming call offer:', data.call_id);
        // Only show if we're not the caller
        if (data.from_user_id !== user?.id) {
          setActiveCall({
            callId: data.call_id,
            roomId: data.room_id,
            isCaller: false,
            status: 'ringing',
            fromUserId: data.from_user_id,
            fromUserName: data.from_user_name || 'Someone'
          });
        }
        return; // Don't add to messages
      }
      
      // Handle video call answer (response to our call)
      if (data.type === 'video_call_answer') {
        log('Call answer received:', data.accepted);
        if (data.accepted) {
          // Navigate both to video room using matchId (not room_id)
          navigate(`/video/${matchId}`);
        } else {
          // Show declined message
          alert('Call declined');
          setActiveCall(null);
        }
        return; // Don't add to messages
      }

      setMessages((prev) => {
        // Avoid duplicates if we already have this message
        if (data.id && prev.some((m) => m.id === data.id)) {
          log(`Duplicate message detected, skipping:`, data.id);
          return prev;
        }

        const newMsg = {
          id: data.id || `${Date.now()}`,
          sender_id: data.senderId,
          content: data.text,
          timestamp: new Date(data.createdAt).getTime() / 1000,
        };
        
        log(`Adding new message, new length:`, prev.length + 1);
        return [...prev, newMsg];
      });
    });
    
    // Subscribe to typing events
    channel.subscribe("typing", (msg) => {
      // CRITICAL: Ignore stale callbacks from previous room
      if (currentEpoch !== roomEpochRef.current) {
        log(`Ignoring stale typing event from epoch ${currentEpoch}`);
        return;
      }
      
      const data = msg.data || {};
      if (!user || data.from_user_id === user.id) return;
      
      // Mark first typing event for Ably metrics
      markFirstTypingReceived();
      
      setOtherIsTyping(!!data.is_typing);
    });

    return () => {
      log(`Cleaning up Ably subscription for ${channelName}`);
      unsubscribe();
      channel.unsubscribe("typing");
      
      // CRITICAL: Detach channel to prevent "Channel detached" errors on room switch
      if (channel && channel.state !== 'detached' && channel.state !== 'failed') {
        channel.detach((err) => {
          if (err && !err.message?.includes('superseded')) {
            log(`Channel detach error (non-fatal):`, err?.message);
          } else {
            log(`Channel ${channelName} detached cleanly`);
          }
          
          // Release channel from Ably client cache (prevents memory leaks during rapid room switching)
          try {
            ablyClient.channels.release(channelName);
            log(`Channel ${channelName} released from cache`);
          } catch (releaseErr) {
            // Non-fatal: channel might already be released
            log(`Channel release error (non-fatal):`, releaseErr?.message);
          }
        });
      }
      
      ablyChannelRef.current = null;
    };
  }, [matchId, ablyClient, user?.id, navigate]);

  // Ticket 25: Subscribe to gift events
  useEffect(() => {
    if (!matchId || !ablyClient) return;
    
    const channelName = `chat:match:${matchId}`;
    log(`Setting up gift subscription for ${channelName}`);
    
    const unsubGift = subscribeToEvent(channelName, 'gift', (giftData) => {
      log(`Received gift event:`, giftData?.gift_key);
      
      // Only show animation if we're the recipient (not the sender)
      if (giftData.to_user_id === user?.id && giftData.from_user_id !== user?.id) {
        triggerGiftAnimation({
          gift_key: giftData.gift_key,
          gift_name: giftData.gift_name,
          gift_emoji: giftData.gift_emoji,
          animation: giftData.gift_key,
          fromUserName: giftData.from_user_name || 'Someone',
          toUserName: 'you',
        });
      }
    });
    
    return () => {
      log(`Cleaning up gift subscription`);
      unsubGift();
    };
  }, [matchId, ablyClient, user?.id, triggerGiftAnimation]);

  // Send read receipt when messages change
  useEffect(() => {
    if (!messages || messages.length === 0 || !user || !sendReadReceipt) return;
    
    const lastMessage = messages[messages.length - 1];
    
    // Only send read receipts for messages from the other person
    if (lastMessage && lastMessage.sender_id !== user.id) {
      sendReadReceipt(lastMessage.id);
    }
  }, [messages, user, sendReadReceipt]);

  // Typing and read receipt logic now handled by hooks

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Check if video chat is available (10+ messages)
  const isVideoAvailable = messages.length >= 10;
  
  // Count messages from current user
  const userMessageCount = messages.filter(msg => msg.sender_id === user.id).length;

  // --- Translation helpers ---
  const getTargetLang = () => {
    // Use browser language (e.g. "en", "es", "fr", "sw")
    try {
      const lang = (navigator?.language || "en").toLowerCase();
      return lang.split("-")[0] || "en";
    } catch {
      return "en";
    }
  };

  const translateMessage = async (message) => {
    if (!message?.id) return;
    if (translatingById[message.id]) return;

    setTranslateErrorById((prev) => ({ ...prev, [message.id]: "" }));
    setTranslatingById((prev) => ({ ...prev, [message.id]: true }));

    try {
      const target_lang = getTargetLang();
      const res = await apiClient.post(
        `/messages/${message.id}/translate`,
        { target_lang },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const translated_text = res?.data?.translated_text || "";

      if (!translated_text) throw new Error("No translation returned.");

      setTranslatedById((prev) => ({ ...prev, [message.id]: translated_text }));
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        "Translation failed.";
      setTranslateErrorById((prev) => ({ ...prev, [message.id]: msg }));
    } finally {
      setTranslatingById((prev) => ({ ...prev, [message.id]: false }));
    }
  };

  const hideTranslation = (messageId) => {
    setTranslatedById((prev) => {
      const copy = { ...prev };
      delete copy[messageId];
      return copy;
    });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    
    // Stop typing indicator when sending
    sendTyping(false);
    
    try {
      // FIX #3: Use centralized api client
      const res = await api.post('/messages', {
        match_id: matchId,
        content: newMessage.trim()
      });

      // Update video unlock status from response (coerce to boolean for safety)
      if (res.data?.video_unlocked !== undefined) {
        setVideoUnlocked(Boolean(res.data.video_unlocked));
        log(`Video unlock status updated from send response:`, Boolean(res.data.video_unlocked));
      }
      
      // Update voice unlock status from response
      if (res.data && typeof res.data.voice_unlocked === 'boolean') {
        setVoiceUnlocked(res.data.voice_unlocked);
        log(`Voice unlock status updated from send response:`, res.data.voice_unlocked);
      }
      if (res.data?.voice_unlock_status) {
        setVoiceUnlockStatus(res.data.voice_unlock_status);
      }

      // Clear input immediately for good UX
      setNewMessage('');
      
      // FIX: Rehydrate messages from server (POST → GET pattern)
      // Don't rely on SSE/Ably for sender's own messages
      try {
        const messagesResponse = await api.get(`/messages/${matchId}?limit=50`);
        const msgs = Array.isArray(messagesResponse.data)
          ? messagesResponse.data
          : (messagesResponse.data?.items?.reverse() ?? []);
        setMessages(msgs);
      } catch (fetchErr) {
        console.warn('[Chat] Failed to refresh messages after send:', fetchErr);
        // Non-critical - message was sent, just won't show until SSE or refresh
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      // Could show an error notification here
    } finally {
      setSending(false);
    }
  };

  const handleVideoInvite = async () => {
    console.log("[VideoCall] handleVideoInvite triggered, matchId:", matchId, "videoUnlocked:", videoUnlocked);
    if (!matchId) {
      console.warn("[VideoCall] No matchId, returning early");
      return;
    }

    try {
      // If frontend thinks it's locked, double-check server truth once
      if (!videoUnlocked) {
        console.log("[VideoCall] videoUnlocked is false, checking server...");
        try {
          const metaRes = await api.get(`/chat/meta/${matchId}`);
          console.log("[VideoCall] Server meta response:", metaRes?.data);
          const serverUnlocked = Boolean(metaRes?.data?.video_unlocked);

          // Update state so UI reflects server truth
          setVideoUnlocked(serverUnlocked);

          if (!serverUnlocked) {
            console.warn("[VideoCall] Video still locked per server meta");
            return;
          }
          log("[VideoCall] Server confirmed video is unlocked, proceeding...");
        } catch (metaErr) {
          console.error("[VideoCall] Failed to refresh video meta:", metaErr);
          return;
        }
      }

      // At this point video is unlocked - proceed with call initiation
      console.log("[VideoCall] Video unlocked, initiating call...");
      setIsStartingVideo(true);

      // FIX #3: Use centralized api client - Initiate video call
      console.log("[VideoCall] Calling /video/call API...");
      const response = await api.post('/video/call', { match_id: matchId });
      console.log("[VideoCall] API response:", response?.data);

      if (response.data) {
        // Store active call info (room_id is for RTC, matchId is for routing)
        setActiveCall({
          callId: response.data.call_id,
          roomId: response.data.room_id,
          isCaller: true,
          status: 'ringing'
        });
        
        log('Call initiated:', response.data.call_id);
        
        // Navigate to video screen using matchId (VideoChat expects matchId in URL)
        // room_id is only used internally for RTC session identification
        console.log("[VideoCall] Navigating to /video/" + matchId);
        navigate(`/video/${matchId}`);
      }
    } catch (err) {
      console.error('[VideoCall] Failed to initiate call:', err);
      
      if (err.response?.status === 403) {
        toast.error(err.response?.data?.detail || 'Video calling is not unlocked yet');
      } else if (err.response?.status === 429) {
        toast.error('Too many requests. Please wait a moment.');
      } else {
        toast.error('Failed to start video call. Please try again.');
      }
    } finally {
      setIsStartingVideo(false);
    }
  };

  const answerVideoCall = async (accept) => {
    if (!activeCall) return;
    
    try {
      // FIX #3: Use centralized api client
      await api.post('/video/answer', {
        call_id: activeCall.callId,
        accept
      });

      if (accept) {
        // Navigate to video room
        navigate(`/video/${matchId}`);
      } else {
        // Just close the modal
        setActiveCall(null);
      }
    } catch (err) {
      console.error('[VideoCall] Failed to answer call:', err);
      alert('Failed to respond to call');
      setActiveCall(null);
    }
  };

  const handleAcceptVideoInvite = async () => {
    try {
      setIsStartingVideo(true);

      // Check if daily limit allows this call
      const result = await requestVideoCallStart(matchId, token);

      if (result.allowed) {
        // ✅ Still within daily limit → start the call
        navigate(`/video/${matchId}`);
        return;
      }

      // ❌ Over the limit → show paywall modal
      setVideoLimitMessage(
        result.message || "You've used your free private video call for today."
      );
      setVideoLimitRemaining(
        typeof result.remaining_calls === 'number' ? result.remaining_calls : null
      );
      setShowVideoLimitModal(true);
    } catch (err) {
      console.error('[VideoCall] accept failed', err);
      setVideoLimitMessage(
        "Something went wrong trying to start your video call. Please try again in a moment."
      );
      setVideoLimitRemaining(null);
      setShowVideoLimitModal(true);
    } finally {
      setIsStartingVideo(false);
    }
  };

  const handleDeclineVideoInvite = () => {
    setVideoInviteReceived(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-warm px-4">
        <div className="text-2xl font-playfair text-rose-600 mb-4">Loading chat...</div>
        <div className="text-sm text-gray-500 mb-6">matchId: {matchId}</div>
        {/* Safety: Show retry after 5s even if loading */}
        <button
          onClick={() => {
            cancelUniverseLoading({ source: "Chat:loading_escape" });
            setLoading(false);
            setLoadError('Loading was taking too long. Please try again.');
          }}
          className="text-sm text-gray-400 hover:text-gray-600 underline"
        >
          Taking too long? Tap to retry
        </button>
      </div>
    );
  }

  // Show error state if load failed - with debug info
  if (loadError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-warm px-4">
        <div className="text-center max-w-md">
          <div className="text-4xl mb-4">💬</div>
          <h2 className="font-playfair text-2xl font-bold text-gray-900 mb-4">
            Chat failed to load
          </h2>
          <p className="text-red-600 font-medium mb-2">{loadError}</p>
          <p className="text-xs text-gray-500 mb-4">matchId: {matchId}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/matches')}
              className="btn-secondary"
            >
              Back to Matches
            </button>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // SAFE: Don't block render if match is still loading - show minimal header instead
  // This prevents "Match not found" flash while metadata is loading in background

  return (
    <div className="min-h-screen bg-gradient-warm flex flex-col">
      {/* Chat Header - Safe fallback when match is still loading */}
      <header className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm border-b border-rose-100 dark:border-slate-700 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <button
              onClick={() => {
                if (window.history.length > 1) navigate(-1);
                else navigate('/universe', { replace: true });
              }}
              className="flex items-center space-x-2 text-gray-700 hover:text-rose-600"
              data-testid="back-to-matches-btn"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="hidden sm:block">Back</span>
            </button>
            
            {/* Explicit inbox escape hatch */}
            <button
              type="button"
              onClick={() => navigate("/inbox")}
              className="ml-2 text-sm px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 text-gray-600 hover:text-rose-600 border border-gray-200"
              data-testid="back-to-inbox-btn"
            >
              Inbox
            </button>
            
            <div className="flex items-center justify-between flex-1">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-rose-gold rounded-full flex items-center justify-center">
                  <span className="font-semibold text-gray-700">
                    {match?.profile?.names?.[0]?.charAt(0) || '💬'}
                  </span>
                </div>
                <div>
                  <div className="font-playfair font-semibold text-gray-900">
                    {match?.profile?.names?.join(' & ') || 'Chat'}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">
                      {match?.profile?.location || (match ? '' : 'Loading...')}
                    </span>
                    {otherOnline && <OnlineDot />}
                  </div>
                </div>
              </div>

              {/* Video Chat Button - Elite can START, anyone can RECEIVE */}
              {isVideoAvailable && (
                !canUseVideoChat ? (
                  // Non-Elite: Show locked button - they can't START but CAN receive
                  <button
                    onClick={() => navigate(getEliteUpgradeUrl('video_call'))}
                    className="p-2 rounded-full bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-600 hover:from-amber-200 hover:to-yellow-200"
                    title="Elite members can start video calls"
                    aria-label="Start video call (Elite feature)"
                    data-testid="video-chat-btn-elite-locked"
                  >
                    <span className="flex items-center gap-1">
                      <Video className="w-5 h-5" />
                      <span className="text-xs">👑</span>
                    </span>
                  </button>
                ) : videoUnlocked ? (
                  // Elite + unlocked: Can start video
                  <button
                    onClick={handleVideoInvite}
                    disabled={videoInviteSent || isStartingVideo}
                    className={`p-2 rounded-full ${
                      videoInviteSent || isStartingVideo
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                        : 'bg-rose-100 text-rose-600 hover:bg-rose-200'
                    }`}
                    title={
                      isStartingVideo 
                        ? 'Checking...' 
                        : videoInviteSent 
                        ? 'Video invite sent' 
                        : 'Start video chat'
                    }
                    data-testid="video-chat-btn"
                  >
                    <Video className="w-5 h-5" />
                  </button>
                ) : (
                  // Elite but need more messages first
                  <button
                    disabled
                    className="p-2 rounded-full bg-gray-200 text-gray-400 cursor-not-allowed opacity-70"
                    title="🔒 Send a few messages each to unlock video"
                    data-testid="video-chat-btn-locked"
                  >
                    <Video className="w-5 h-5" />
                  </button>
                )
              )}

              {/* Ticket 14: Moderation menu */}
              <div className="relative">
                <button
                  onClick={() => setShowMoreMenu((v) => !v)}
                  className="ml-2 p-2 rounded-full hover:bg-gray-100 text-gray-600"
                  title="More options"
                >
                  •••
                </button>
                {showMoreMenu && (
                  <div className="absolute right-0 mt-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg shadow-lg text-sm z-20 w-48">
                    {/* Give Private Gallery Access */}
                    <button
                      onClick={async () => {
                        setShowMoreMenu(false);
                        const partnerId = match?.user_ids?.find(id => id !== user.id);
                        if (!partnerId) return;
                        setGrantingPrivateAccess(true);
                        try {
                          await grantPrivateGalleryAccess(partnerId);
                          setPrivateAccessGranted(true);
                          // State persists - no timeout reset needed
                        } catch (e) {
                          console.error(e);
                          alert('Could not grant access right now. Try again.');
                        } finally {
                          setGrantingPrivateAccess(false);
                        }
                      }}
                      disabled={grantingPrivateAccess || privateAccessGranted}
                      className={`flex items-center gap-2 w-full text-left px-4 py-2 ${
                        privateAccessGranted 
                          ? 'bg-emerald-50 text-emerald-700' 
                          : 'hover:bg-gray-50'
                      } disabled:opacity-70`}
                    >
                      <Lock className="w-4 h-4" />
                      {grantingPrivateAccess ? 'Granting...' : privateAccessGranted ? '✓ Access Granted' : 'Give Private Access'}
                    </button>
                    <hr className="border-gray-100" />
                    <button
                      onClick={() => {
                        setShowMoreMenu(false);
                        setShowReportModal(true);
                      }}
                      className="block w-full text-left px-4 py-2 hover:bg-gray-50"
                    >
                      Report user
                    </button>
                    <button
                      onClick={async () => {
                        setShowMoreMenu(false);
                        try {
                          const partnerId = match?.user_ids?.find(id => id !== user.id);
                          await blockUser({ targetUserId: partnerId });
                          alert('They won\'t be able to contact you again.');
                          navigate('/matches');
                        } catch (e) {
                          console.error(e);
                          alert('Failed to block user');
                        }
                      }}
                      className="block w-full text-left px-4 py-2 hover:bg-gray-50 text-red-600"
                    >
                      Block user
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="w-20"></div> {/* Spacer for centering */}
          </div>
        </div>
      </header>

      {/* Messages Container */}
      <div className="flex-1 overflow-hidden">
        <div className="max-w-4xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-6 h-full">
          <div className="glass rounded-2xl h-full flex flex-col">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="messages-container">
              {messages.length === 0 ? (
                <div className="text-center py-12">
                  {/* P4: Conversation Starters for empty threads */}
                  <ConversationStarters onPick={(txt) => setNewMessage((d) => d ? d : txt)} />
                  <div className="text-gray-500 mb-4 mt-4">
                    {Array.isArray(match?.profile?.names) && match.profile.names.length > 0 
                      ? `You matched with ${match.profile.names.join(' & ')}!` 
                      : 'Start a conversation!'}
                  </div>
                  <div className="text-gray-400 text-sm">
                    Pick a starter above or write your own message below
                  </div>
                </div>
              ) : (
                <>
                  {/* Private Gallery Sharing */}
                  {match?.private_sharing && (
                    <div className="mb-4">
                      <SharePrivateGalleryButton
                        matchId={matchId}
                        token={token}
                        iShared={match.private_sharing.i_shared}
                        theyShared={match.private_sharing.they_shared}
                        onUpdate={(newSharing) => {
                          setMatch({
                            ...match,
                            private_sharing: newSharing
                          });
                        }}
                      />
                    </div>
                  )}

                  {/* Post-match Starters - shows for chats with < 4 messages */}
                  <SuggestedStarters
                    userId={user?.id}
                    matchId={matchId}
                    messagesLen={messages.length}
                    matchProfile={match?.profile}
                    onInsert={(text) => setNewMessage(text)}
                    trackEvent={trackEvent}
                  />

                  {/* Video Chat Progress Indicator */}
                  {messages.length < 10 && (
                    <div className="text-center py-4">
                      <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                        <div className="flex items-center justify-center space-x-2 mb-2">
                          <Video className="w-5 h-5 text-blue-600" />
                          <span className="font-semibold text-blue-700">Video Chat Unlocks Soon!</span>
                        </div>
                        <p className="text-blue-600 text-sm">
                          Exchange {10 - messages.length} more messages to unlock video chat
                        </p>
                        <div className="w-full bg-blue-200 rounded-full h-2 mt-3">
                          <div 
                            className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${(messages.length / 10) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Video Chat Unlocked Notification */}
                  {messages.length === 10 && !videoInviteSent && (
                    <div className="text-center py-4">
                      <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                        <div className="flex items-center justify-center space-x-2 mb-2">
                          <Video className="w-5 h-5 text-green-600" />
                          <span className="font-semibold text-green-700">🎉 Video Chat Unlocked!</span>
                        </div>
                        <p className="text-green-600 text-sm">
                          You can now invite each other to video chat. Look for the video button in the header!
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Messages */}
                  {(() => {
                    // Compute receipt indices for status display
                    const lastSeenIndex = messages.findIndex(m => m.id === partnerLastReadId);
                    const lastDeliveredIndex = messages.findIndex(m => m.id === partnerLastDeliveredId);
                    
                    return messages.map((message, index) => (
                    <div key={message.id || index}>
                      {message.message_type === 'video_invite' ? (
                        // Video Invite Message
                        <div className="text-center py-4">
                          <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg max-w-sm mx-auto">
                            <div className="flex items-center justify-center space-x-2 mb-3">
                              <Video className="w-6 h-6 text-purple-600" />
                              <span className="font-semibold text-purple-700">Video Chat Invitation</span>
                            </div>
                            
                            {message.sender_id === user.id ? (
                              <p className="text-purple-600 text-sm">
                                You sent a video chat invitation
                              </p>
                            ) : (
                              <>
                                <p className="text-purple-600 text-sm mb-4">
                                  {match?.profile?.names?.join(' & ') || 'They'} invited you to video chat
                                </p>
                                <div className="flex space-x-3 justify-center">
                                  <button
                                    onClick={handleAcceptVideoInvite}
                                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-full text-sm flex items-center space-x-1"
                                    data-testid="accept-video-invite"
                                  >
                                    <Check className="w-4 h-4" />
                                    <span>Accept</span>
                                  </button>
                                  <button
                                    onClick={handleDeclineVideoInvite}
                                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-full text-sm flex items-center space-x-1"
                                    data-testid="decline-video-invite"
                                  >
                                    <X className="w-4 h-4" />
                                    <span>Decline</span>
                                  </button>
                                </div>
                              </>
                            )}
                            
                            <div className="text-xs text-purple-500 mt-2">
                              {new Date(message.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                        </div>
                      ) : (
                        // Regular Message
                        <div
                          className={`chat-message-enter flex ${message.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                          data-testid={`message-${index}`}
                        >
                          <div
                            className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
                              message.sender_id === user.id
                                ? 'message-sent'
                                : 'message-received'
                            }`}
                          >
                            {/* Ticket 10: Audio message rendering */}
                            {message.type === "audio" ? (
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                    message.sender_id === user.id
                                      ? 'bg-white/20 hover:bg-white/30 text-white'
                                      : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const audio = new Audio(`${BACKEND_URL}${message.audio_url}`);
                                    audio.play().catch(err => {
                                      console.error("Failed to play audio:", err);
                                    });
                                  }}
                                >
                                  ▶
                                </button>
                                <span className={`text-xs ${
                                  message.sender_id === user.id ? 'text-white/80' : 'text-gray-600'
                                }`}>
                                  Voice message · {Math.round((message.duration_ms || 0) / 1000)}s
                                </span>
                              </div>
                            ) : (
                              <>
                                {/* Author attribution using centralized resolution */}
                                {(() => {
                                  const { displayName, isCoupleMessage, isFromCurrentUser } = resolveMessageAuthor(message, {
                                    currentUserId: user?.id,
                                  });
                                  
                                  // Show author label for received couple messages
                                  if (!isFromCurrentUser && isCoupleMessage) {
                                    return (
                                      <div className="text-[10px] text-purple-400 font-medium mb-1" data-testid="chat-message-author">
                                        {displayName} <span className="text-gray-400">(for the couple)</span>
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                                <p className="text-sm">{message.content}</p>

                                {/* Translate button: ONLY for received text messages */}
                                {message.sender_id !== user.id && message.content && message.content.trim() !== "" && (
                                  <div className="mt-1.5">
                                    {/* Translate / Hide toggle */}
                                    {!translatedById[message.id] ? (
                                      <button
                                        type="button"
                                        className="text-[11px] text-gray-500 hover:text-gray-700 underline underline-offset-2"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          translateMessage(message);
                                        }}
                                        disabled={!!translatingById[message.id]}
                                      >
                                        {translatingById[message.id] ? `${t("common.loading")}` : t("chat.translate")}
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className="text-[11px] text-gray-500 hover:text-gray-700 underline underline-offset-2"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          hideTranslation(message.id);
                                        }}
                                      >
                                        {t("chat.hide_translation")}
                                      </button>
                                    )}

                                    {/* Error message */}
                                    {translateErrorById[message.id] && (
                                      <div className="mt-1 text-[10px] text-red-500">
                                        {translateErrorById[message.id]}
                                      </div>
                                    )}

                                    {/* Translated text */}
                                    {translatedById[message.id] && (
                                      <div className="mt-2 rounded-lg bg-slate-100 border border-slate-200 p-2">
                                        <div className="text-[9px] uppercase tracking-wider text-slate-500 mb-0.5">
                                          {t("chat.translated")}
                                        </div>
                                        <div className="text-sm text-slate-800">
                                          {translatedById[message.id]}
                                        </div>
                                        <div className="text-[9px] text-slate-400 mt-1 italic">
                                          {t("chat.translation_disclaimer")}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            )}
                            <div className={`text-xs mt-1 flex items-center gap-1 ${
                              message.sender_id === user.id ? 'text-white/70' : 'text-gray-500'
                            }`}>
                              <span>
                                {new Date(message.timestamp).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                              {message.sender_id === user.id && (() => {
                                // Determine status based on receipt indices
                                let statusLabel = "Sent";
                                
                                if (lastSeenIndex !== -1 && index <= lastSeenIndex) {
                                  statusLabel = "Seen";
                                } else if (lastDeliveredIndex !== -1 && index <= lastDeliveredIndex) {
                                  statusLabel = "Delivered";
                                }
                                
                                return (
                                  <span className={`text-[10px] ${
                                    statusLabel === "Seen" ? "text-white/60" : "text-white/40"
                                  }`}>
                                    • {statusLabel}
                                  </span>
                                );
                              })()}
                            </div>

                            {/* Ticket 9: Reactions row (summary) */}
                            {Object.keys(reactionsObj(message)).length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1 text-xs">
                                {getReactionSummary(message).map(({ emoji, count }) => (
                                  <span
                                    key={emoji}
                                    className={`px-1.5 py-0.5 rounded-full flex items-center gap-1 ${
                                      message.sender_id === user.id
                                        ? 'bg-white/10 text-white/90'
                                        : 'bg-gray-100 text-gray-700'
                                    }`}
                                  >
                                    <span>{emoji}</span>
                                    <span className="text-[10px]">{count}</span>
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* FB-style Reaction Picker (portal-based for viewport clamping) */}
                            <div className="mt-1 flex gap-2 items-center">
                              {/* Trigger button - shows user's own reaction if they reacted */}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (reactionUI.openFor === message.id) closeReactionPicker();
                                  else openReactionPicker(message.id, e.currentTarget);
                                }}
                                className={`reaction-trigger px-2 py-1 rounded-full text-[11px] border transition-colors ${
                                  message.sender_id === user.id
                                    ? 'bg-slate-900/60 text-slate-200 border-slate-700/60 hover:bg-slate-900/80'
                                    : 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200'
                                }`}
                                title="React to this message"
                              >
                                {(() => {
                                  const reactions = message.reactions || {};
                                  const myEmoji = Object.keys(reactions).find((emoji) =>
                                    (reactions[emoji] || []).includes(user.id)
                                  );
                                  return (
                                    <>
                                      {myEmoji || "😅"} <span className="ml-0.5 align-middle text-[9px]">▾</span>
                                    </>
                                  );
                                })()}
                              </button>
                              
                              {/* Reaction Picker Portal (fixed to viewport, clamped) */}
                              {reactionUI.openFor === message.id &&
                                createPortal(
                                  <>
                                    {/* Backdrop - touch-action:none prevents page pan on tap */}
                                    <div
                                      className="fixed inset-0 z-[9998]"
                                      style={{ touchAction: 'none' }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        closeReactionPicker();
                                      }}
                                      onTouchStart={(e) => {
                                        e.stopPropagation();
                                        closeReactionPicker();
                                      }}
                                    />

                                    {/* Picker - touch-action:none + overscroll-behavior:contain prevents pan gestures */}
                                    <div
                                      className="fixed z-[9999]"
                                      style={{
                                        top: `${reactionUI.top}px`,
                                        left: `${reactionUI.left}px`,
                                        width: "min(340px, calc(100vw - 16px))",
                                        touchAction: 'none',
                                        overscrollBehavior: 'contain',
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <div
                                        className={`flex items-center gap-1 rounded-full px-2 py-1.5 shadow-xl border overflow-x-auto ${
                                          message.sender_id === user.id
                                            ? "bg-slate-900 border-slate-700"
                                            : "bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600"
                                        }`}
                                        style={{ touchAction: 'pan-x' }}
                                      >
                                        {REACTIONS.map((reaction) => {
                                          const isMine = userHasReaction(message, reaction.emoji, user.id);
                                          return (
                                            <button
                                              key={reaction.key}
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                toggleReaction(message.id, reaction.emoji);
                                                closeReactionPicker();
                                              }}
                                              className={`reaction-emoji text-[22px] leading-none hover:scale-110 transition-transform rounded-full px-1 flex-shrink-0 ${
                                                isMine ? "ring-2 ring-emerald-500" : ""
                                              }`}
                                              title={isMine ? `Remove ${reaction.emoji}` : `React with ${reaction.emoji}`}
                                            >
                                              {reaction.emoji}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </>,
                                  document.body
                                )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    ));
                  })()}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div className="border-t border-gray-200 px-1 sm:px-4 py-3 sm:py-4 mobile-safe-bottom">
              {/* Typing indicator */}
              {(isPartnerTyping || otherIsTyping) && (
                <div className="px-4 pb-1 text-[11px] italic text-slate-400">
                  They&apos;re typing…
                </div>
              )}
              
              {/* Ticket 10: Recording status */}
              {isRecording && (
                <div className="px-2 py-2 mb-2 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-sm text-red-700 font-medium">
                      Recording {Number.isFinite(recordingDuration) && recordingDuration >= 0 ? Math.floor(recordingDuration / 1000) : 0}s
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={cancelRecording}
                    className="text-xs text-red-600 hover:text-red-800 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              )}

              {audioError && (
                <div className="px-2 py-2 mb-2 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-700">
                  {audioError}
                </div>
              )}

              {/* P2: Inner Work Nudge Banner - shows after 12h inactivity OR +10 messages */}
              {canShowInnerNudge && (
                <div className="mb-3 p-3 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100 rounded-xl flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-purple-900">
                      {nudgeReason === "inactivity_12h" 
                        ? "It's been a while. What's on your mind?" 
                        : "Great conversation! Want to reflect on it?"}
                    </div>
                    <div className="text-xs text-purple-600 mt-0.5">
                      Take a moment to process privately before continuing.
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={showInnerWork}
                      className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-medium rounded-lg transition"
                    >
                      Reflect
                    </button>
                    <button
                      type="button"
                      onClick={dismissInnerNudge}
                      className="p-1.5 text-purple-400 hover:text-purple-600 hover:bg-purple-100 rounded-lg transition"
                      title="Dismiss"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              <form onSubmit={handleSendMessage} className="flex items-end gap-1 sm:gap-3 w-full overflow-hidden">
                {/* Composer Assist - helps continue conversations */}
                <div className="flex items-center justify-start shrink-0">
                  <ComposerAssist
                    onInsertText={(text) => setNewMessage(text)}
                    onOpenInnerWork={() => setInnerOpen(true)}
                    messageCount={messages.length}
                    lastMessageTime={messages.length > 0 ? (messages[messages.length - 1]?.timestamp || messages[messages.length - 1]?.created_at) : null}
                    matchId={matchId}
                    userId={user?.id}
                  />
                </div>

                {/* Textarea wrapper - flex-1 basis-0 min-w-0 prevents portrait collapse */}
                <div className="flex-1 basis-0 min-w-0">
                  <textarea
                    value={newMessage}
                    onChange={handleInputChange}
                    placeholder="Type a message..."
                    className="w-full min-w-0 form-input rounded-2xl px-3 py-2.5 sm:px-4 sm:py-3 text-base leading-5 min-h-[44px] sm:min-h-[48px] max-h-[140px] resize-none overflow-y-auto bg-white text-gray-900 placeholder-gray-400 dark:bg-gray-900 dark:text-gray-100 dark:placeholder-gray-500"
                    disabled={sending || isRecording}
                    data-testid="message-input"
                    rows={1}
                    onInput={(e) => {
                      // auto-grow
                      e.currentTarget.style.height = "auto";
                      e.currentTarget.style.height = `${Math.min(e.currentTarget.scrollHeight, 140)}px`;
                    }}
                    onKeyDown={(e) => {
                      // Submit on Enter (without Shift)
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (newMessage.trim() && !sending && !isRecording) {
                          handleSendMessage(e);
                        }
                      }
                    }}
                  />
                </div>

                {/* Right-side buttons - flex-shrink-0 to prevent stealing width from textarea */}
                <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                  {/* Ticket 10: Mic button for voice messages - Identity gated + Mutual gated */}
                  <button
                    type="button"
                    onClick={async () => {
                      // IDENTITY GATE: Track demand + soft redirect for unverified users
                      if (!canSendVoice) {
                        trackEvent('voice_attempted_unverified', { match_id: matchId });
                        trackEvent('verify_cta_clicked_from_voice', { match_id: matchId });
                        // Soft redirect to verification - no modal, just gravity
                        navigate(`/verify-age?return=/chat/${matchId}`);
                        return;
                      }
                      
                      // MUTUAL VOICE GATE: Both users must have sent >= 3 messages
                      if (!voiceUnlocked) {
                        trackEvent('voice_attempted_locked', { 
                          match_id: matchId,
                          you_sent: voiceUnlockStatus.you_sent,
                          they_sent: voiceUnlockStatus.they_sent,
                        });
                        // Show helpful message with progress
                        alert(
                          `Voice unlocks after you've both sent 3 messages.\n\nYou: ${voiceUnlockStatus.you_sent || 0}/3\nThem: ${voiceUnlockStatus.they_sent || 0}/3`
                        );
                        return;
                      }
                      
                      if (isRecording) {
                        // Stop and send
                        const result = await stopRecording();
                        if (!result) return; // too short or cancelled

                        const { blob, durationMs } = result;

                        try {
                          const formData = new FormData();
                          formData.append("duration_ms", String(durationMs));
                          formData.append("file", blob, "voice-message.webm");

                          const res = await fetch(
                            `/chat/${matchId}/messages/audio`,
                            {
                              method: "POST",
                              headers: {
                                Authorization: `Bearer ${token}`,
                              },
                              body: formData,
                            }
                          );

                          if (!res.ok) {
                            const errorData = await res.json().catch(() => ({}));
                            debugLog('audio', 'Upload failed', { status: res.status, errorData });
                            
                            // Handle MUTUAL_VOICE_REQUIRED error
                            if (errorData?.detail?.reason_code === 'MUTUAL_VOICE_REQUIRED') {
                              alert(
                                `Voice is locked.\n\nYou: ${errorData.detail.you_sent || 0}/3\nThem: ${errorData.detail.they_sent || 0}/3\n\n${errorData.detail.human_hint || ''}`
                              );
                              return;
                            }
                            
                            console.error("Failed to send audio message", errorData);
                            return;
                          }

                          const message = await res.json();
                          debugLog('audio', 'Upload success', { messageId: message.id, matchId });
                          // Add to messages list (Ably will also deliver it)
                          setMessages(prev => [...prev, message]);
                        } catch (err) {
                          debugLog('audio', 'Upload error', { error: err.message });
                          console.error("Error uploading audio message", err);
                        }
                      } else {
                        // Start recording
                        await startRecording();
                      }
                    }}
                    aria-disabled={!canSendVoice || !voiceUnlocked}
                    className={`relative w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition ${
                      !canSendVoice || !voiceUnlocked
                        ? "bg-gray-300 text-gray-400 opacity-50 cursor-not-allowed"
                        : isRecording
                        ? "bg-red-600 text-white animate-pulse"
                        : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                    }`}
                    title={
                      !canSendVoice
                        ? "Verify your identity to send voice notes"
                        : !voiceUnlocked
                        ? `Voice locked (You: ${voiceUnlockStatus.you_sent || 0}/3 • Them: ${voiceUnlockStatus.they_sent || 0}/3)`
                        : isRecording
                        ? "Tap to stop & send"
                        : "Tap to record voice message"
                    }
                    data-testid="voice-record-btn"
                  >
                    {/* Lock badge for voice unlock */}
                    {canSendVoice && !voiceUnlocked && (
                      <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[8px] text-white">
                        🔒
                      </span>
                    )}
                    {isRecording ? (
                      <span className="text-[10px] font-bold">REC</span>
                    ) : (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-4 h-4 sm:w-5 sm:h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                        />
                      </svg>
                    )}
                  </button>

                  {/* Ticket 24: Gift button - visible on mobile (icon-only) */}
                  <button
                    type="button"
                    onClick={() => setShowGiftModal(true)}
                    disabled={isRecording}
                    className="inline-flex items-center justify-center rounded-full bg-pink-600 hover:bg-pink-500 text-white w-9 h-9 text-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Send a virtual gift"
                    aria-label="Send a virtual gift"
                  >
                    💜
                  </button>

                  <button
                    type="submit"
                    disabled={sending || !newMessage.trim() || isRecording}
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center bg-pink-600 hover:bg-pink-500 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="send-message-btn"
                  >
                  {sending ? (
                    <div className="spinner w-4 h-4 border-2 border-white"></div>
                  ) : (
                    <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Video Limit Modal */}
      <VideoLimitModal
        open={showVideoLimitModal}
        onClose={() => setShowVideoLimitModal(false)}
        message={videoLimitMessage}
        remainingMinutes={videoLimitRemaining}
      />

      {/* Incoming Video Call Modal */}
      {activeCall && !activeCall.isCaller && activeCall.status === 'ringing' && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl animate-pulse-slow">
            <div className="mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full mx-auto mb-4 flex items-center justify-center">
                <Video className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Incoming Video Call
              </h3>
              <p className="text-gray-600">
                {activeCall.fromUserName} is calling...
              </p>
            </div>
            
            <div className="flex gap-4">
              <button
                onClick={() => answerVideoCall(false)}
                className="flex-1 px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold transition flex items-center justify-center gap-2"
              >
                <X className="w-5 h-5" />
                Decline
              </button>
              <button
                onClick={() => answerVideoCall(true)}
                className="flex-1 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-semibold transition flex items-center justify-center gap-2"
              >
                <Video className="w-5 h-5" />
                Accept
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Calling Modal (when you initiated the call) */}
      {activeCall && activeCall.isCaller && activeCall.status === 'ringing' && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl">
            <div className="mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full mx-auto mb-4 flex items-center justify-center animate-pulse">
                <Phone className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Calling...
              </h3>
              <p className="text-gray-600">
                Waiting for {match?.profile?.names?.[0] || 'them'} to answer
              </p>
            </div>
            
            <button
              onClick={() => setActiveCall(null)}
              className="w-full px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Patch B2: Report & Block Modal */}
      <ReportBlockModal
        open={showReportModal}
        onClose={() => {
          setShowReportModal(false);
          setReportReason('');
        }}
        targetUserId={(() => {
          const ids = match?.user_ids || [];
          const found = ids.find((id) => id && id !== user?.id);
          if (found) return found;
          if (!match?.user1_id || !match?.user2_id || !user?.id) return null;
          return match.user1_id === user.id ? match.user2_id : match.user1_id;
        })()}
        targetUserName={match?.profile?.names?.join(' & ') || 'this user'}
        matchId={matchId}
        onBlocked={() => {
          // Navigate away after blocking
          navigate('/matches');
        }}
      />

      {/* Ticket 24: Gift Picker Modal */}
      <GiftPickerModal
        open={showGiftModal}
        onClose={() => setShowGiftModal(false)}
        targetUserId={otherUserId}
        matchId={matchId}
        recipientName={match?.profile?.names?.join(' & ') || 'them'}
      />

      {/* Inner Work Modal - opened from Composer Assist or Nudge */}
      <InnerWorkModal
        open={innerOpen}
        onClose={() => setInnerOpen(false)}
        prompt={innerPrompt}
        onCreated={() => {
          // P2: Track when reflection is saved via modal
          if (user?.id && matchId) {
            markNudgeUsed({
              userId: user.id,
              matchId,
              messagesLength: Array.isArray(messages) ? messages.length : 0,
            });
            trackEvent("assist_used", {
              source: "inner_work_modal_saved",
              match_id: matchId,
            });
          }
        }}
        title="Inner Work — Reflection"
      />
    </div>
  );
};

export default Chat;